import type { OverviewFishGroupSummary } from '@/domain/fishGroups/types';

import type { MutationResult, QueryResult } from '../shared/types';

export const LOCATION_TYPE_SHORT_NAMES = {
    DEFAULT: 'default',
    SMOLT: 'smolt_location',
    LANDBASED: 'landbased',
} as const;

export type LocationTypeShortName =
    (typeof LOCATION_TYPE_SHORT_NAMES)[keyof typeof LOCATION_TYPE_SHORT_NAMES];

export const CAGE_TYPE_NAMES = {
    OPEN_IN_SEA: 'open_in_sea',
    SUBMERGED: 'submerged',
    SMOLT_PRODUCTION: 'smolt_production',
    CLOSED_IN_SEA: 'closed_in_sea',
    LANDBASED: 'landbased',
    OTHER: 'other',
} as const;

export type CageTypeName = (typeof CAGE_TYPE_NAMES)[keyof typeof CAGE_TYPE_NAMES];

/** `fishTypes.name` values from the DB. Only the ones we branch on are listed. */
export const FISH_TYPE_NAMES = {
    TROUT: 'Trout',
} as const;

/**
 * `fishTypes.shortName` values that carry behaviour of their own.
 *
 * Adding a species is a database insert; it needs an entry here only if something has to treat
 * it differently. Wounds are a salmon measure and the wound footnote is about trout, which is
 * why those two appear and nothing else does.
 */
export const FISH_TYPE_SHORT_NAMES = {
    SALMON: 'salmon',
    TROUT: 'trout',
} as const;

export type FishTypeName = (typeof FISH_TYPE_NAMES)[keyof typeof FISH_TYPE_NAMES];

/** `liceType.shortName` values from the DB. Only the ones we branch on are listed. */
export const LICE_TYPE_SHORT_NAMES = {
    CHILEAN: 'chilean_lice',
} as const;

export type LiceTypeShortName = (typeof LICE_TYPE_SHORT_NAMES)[keyof typeof LICE_TYPE_SHORT_NAMES];

export interface CageType {
    id: number;
    name: string;
    isDefault: boolean;
}

export interface UseGetCageTypesResult extends QueryResult<CageType[]> {}

export interface CreateCagesRequest {
    locationId: number;
    cageTypeId: number;
    /** Species for every pen in this batch. A location may hold several (WB-2207). */
    fishTypeId?: number;
    names: string[];
    externalName?: string;
    externalId?: string;
}

export interface CreateCagesVariables {
    requestBody: CreateCagesRequest;
}

export interface CreatedCagesResult {
    ids: number[];
}

export interface DeleteCageVariables {
    cageId: number;
}

export interface UpdateCageRequest {
    name: string;
    /** Omitted leaves the cage's current type untouched. */
    cageTypeId?: number;
    /** Species in this pen. Omit to leave it unchanged (WB-2207). */
    fishTypeId?: number;
    externalName?: string;
    externalId?: string;
}

export interface UpdateCageVariables {
    cageId: number;
    requestBody: UpdateCageRequest;
}

export interface UpdateCageResult {
    id: number;
}

export type CageListStatus = 'active' | 'inactive' | 'historical';

export interface CageBioscopeHistoryItem {
    /**
     * Unique within the pen's history. The API sends no placement id, and a bioscope can be
     * placed in the same pen twice on one day, so bioscope and day alone can repeat.
     */
    key: string;
    bioscopeId: number;
    from: string;
    to: string | null;
}

export interface CageListItem {
    id: number;
    name: string;
    status: CageListStatus;
    historicalAt: string | null;
    cageType: string | null;
    /** Species in this pen, not the location default. A location can hold several (WB-2207). */
    fishType: string | null;
    externalId: string | null;
    externalName: string | null;
    clientId: number | null;
    clientName: string | null;
    locationId: number | null;
    locationName: string | null;
    currentBioscopeId: number | null;
    bioscopeHistory: CageBioscopeHistoryItem[];
    firstMeasurement: string | null;
    lastMeasurement: string | null;
    /**
     * The cohort in this pen now, `null` for an empty pen. A link into the pen carries
     * its id; without one the pen opens on its pen-wide legacy view.
     */
    activeFishGroup: OverviewFishGroupSummary | null;
}

// Domain types for CageStatus
export interface DomainCageStatus {
    currentBioscope: CurrentBioscopeInfo;
    historicalBioscopes: HistoricalBioscopeInfo[];
    lastData: LastDataInfo;
}

export interface CurrentBioscopeInfo {
    id?: number;
    lastAlive?: string;
}

export interface HistoricalBioscopeInfo {
    bioscopeId: number;
    from: string;
    /** Null while the bioscope is still deployed, although the published DTO declares a string. */
    to: string | null;
}

export interface LastDataInfo {
    depthLast?: number;
    lastAlive?: string;
    lastMoveFromDate?: string;
    pitchLast?: number;
    rollLast?: number;
    timestampLast?: string;
}

// Domain types for FishTypes
export interface DomainFishType {
    id: number;
    name: string;
    shortName: string;
    isDefault: boolean;
}

// Result types

export interface UseGetCageStatusResult extends QueryResult<DomainCageStatus | null> {}
export interface UseGetFishTypesResult extends QueryResult<DomainFishType[]> {}
export interface UseGetAllCagesResult extends QueryResult<CageListItem[]> {}
export interface UseCreateManyCagesResult
    extends MutationResult<CreatedCagesResult, CreateCagesVariables> {}
export interface UseUpdateCageResult
    extends MutationResult<UpdateCageResult, UpdateCageVariables> {}
export interface UseDeleteCageResult extends MutationResult<void, DeleteCageVariables> {}
